import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory cache so hovering the same @nickname repeatedly doesn't refetch.
// Kept module-scoped intentionally — cleared on page reload.
const cache = new Map();

const formatJoined = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-CA", {
            year: "numeric",
            month: "short",
        });
    } catch {
        return "";
    }
};

// A styled @mention pill that reveals a small profile card on hover / focus.
// The card is best-effort — 404 nicknames render as a plain pill with no card.
export default function MentionPill({ nickname }) {
    const [open, setOpen] = useState(false);
    const [profile, setProfile] = useState(cache.get(nickname) || null);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const openTimer = useRef(null);
    const closeTimer = useRef(null);

    const load = async () => {
        if (profile || notFound || loading) return;
        setLoading(true);
        try {
            const r = await axios.get(
                `${API}/users/nickname/${encodeURIComponent(nickname)}`,
            );
            cache.set(nickname, r.data);
            setProfile(r.data);
        } catch (err) {
            if (err.response?.status === 404) setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const scheduleOpen = () => {
        clearTimeout(closeTimer.current);
        openTimer.current = setTimeout(() => {
            setOpen(true);
            load();
        }, 120);
    };
    const scheduleClose = () => {
        clearTimeout(openTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), 180);
    };

    useEffect(
        () => () => {
            clearTimeout(openTimer.current);
            clearTimeout(closeTimer.current);
        },
        [],
    );

    return (
        <span
            className="relative inline-block"
            onMouseEnter={scheduleOpen}
            onMouseLeave={scheduleClose}
            onFocus={scheduleOpen}
            onBlur={scheduleClose}
        >
            <button
                type="button"
                data-testid="mention-pill"
                onClick={() => setOpen((v) => !v)}
                className="inline-block font-mono font-semibold text-[12px] tracking-[0.02em] bg-[color:var(--charcoal)]/10 hover:bg-[color:var(--charcoal)]/20 focus:bg-[color:var(--charcoal)]/20 text-[color:var(--charcoal)] px-1.5 py-0.5 rounded-sm transition-colors outline-none"
                title={`Mention of @${nickname}`}
            >
                @{nickname}
            </button>
            <AnimatePresence>
                {open && (profile || notFound || loading) && (
                    <motion.div
                        role="tooltip"
                        data-testid={`mention-card-${nickname}`}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 4 }}
                        transition={{ duration: 0.16 }}
                        className="absolute z-40 top-full left-0 mt-1 w-[280px] max-w-[calc(100vw-2rem)] bg-[color:var(--cloud)] border border-[color:var(--charcoal)] shadow-[0_16px_32px_rgba(13,19,23,0.15)] p-4 font-body text-sm text-[color:var(--charcoal)]"
                    >
                        {loading && (
                            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                                Loading…
                            </div>
                        )}
                        {notFound && (
                            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                                @{nickname} isn&apos;t a member (yet).
                            </div>
                        )}
                        {profile && (
                            <>
                                <div className="flex items-baseline justify-between gap-2 mb-1">
                                    <span className="font-display italic text-lg leading-tight">
                                        {profile.name || `@${profile.nickname}`}
                                    </span>
                                    {profile.role === "super_admin" && (
                                        <span
                                            className="font-mono text-[10px] uppercase tracking-[0.2em] px-1.5 py-0.5 bg-[color:var(--charcoal)] text-white"
                                            title="Founder / super admin"
                                        >
                                            Founder
                                        </span>
                                    )}
                                </div>
                                <div className="font-mono font-semibold text-[11px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-3">
                                    @{profile.nickname}
                                    {profile.joined && ` · joined ${formatJoined(profile.joined)}`}
                                    {typeof profile.comment_count === "number" && ` · ${profile.comment_count} ${profile.comment_count === 1 ? "comment" : "comments"}`}
                                </div>
                                {profile.recent_comments?.length > 0 ? (
                                    <ul className="border-t border-[color:var(--charcoal)]/15 pt-3 space-y-2.5">
                                        {profile.recent_comments.slice(0, 3).map((c) => (
                                            <li
                                                key={c.id}
                                                className="text-[13px] leading-[1.5] text-[color:var(--charcoal)]/90"
                                            >
                                                {c.is_reply && (
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--slate)] mr-1.5">
                                                        Reply ·
                                                    </span>
                                                )}
                                                &ldquo;{c.body}&rdquo;
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-[color:var(--charcoal)]/60 italic text-[13px] border-t border-[color:var(--charcoal)]/15 pt-3">
                                        No public comments yet.
                                    </p>
                                )}
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </span>
    );
}
